import React, {useEffect, useMemo, useState} from 'react';
import HStack from '../../../../../common/Layout/HStack';
import VStack from '../../../../../common/Layout/VStack';
import AppText from "../../../../../common/AppText";
import {GlobalStyleAttributes} from "../../../../../../styles/GlobalStyles";
import {t} from "../../../../../../services/i18n";
import AdvanceSelect from "../../../../../common/AdvanceSelect";
import {Colors} from "../../../../../../styles/Colors";
import {
    autoMessageTypesObj,
    EMAIL,
    PUSH,
    SMS,
    WHATSAPP
} from "../../../../../../services/ConstHelper";
import {fetchApi} from "../../../../../../services/HTTP";
import usePermissions from "../../../../../../services/Permissions";
import MessageTextFormInput from "../../../../../common/MessageTextFormInput";
import {useDispatch, useSelector} from "react-redux";
import {AUTO_ACTION_APPLY_TO_MODAL, openModal} from "../../../../../../redux/modalManagerSlice";
import SimpleInput from "../../../../../common/SimpleInput";
import CustomSwitch from "../../../../../common/CustomSwitch";
import {Divider, Radio, Segmented} from "antd";
import {dbTimeFormat, isMobile} from "../../../../../../services/Helpers";
import moment from "moment";
import SharedTimepicker from "../../../../../common/SharedTimepicker";
import {
    AUTO_ACTION_TRIGGERS,
    AUTO_ACTION_TRIGGER_KEYS,
    DEFAULT_TIMING_HOUR,
    buildAutoActionRelationFromRow,
    getAutoActionApplyToRelation,
    getAutoActionMessageTags,
    getMessageTypeAutoActions,
    getDefaultPeriodAmount,
    getDefaultPeriodTimeUnit,
} from "../../../../../../../web/src/components/AutoActions/AutoActionsConfig";
import DropDownMenu from "../../../../../common/DropDownMenu";
import {autoMessageTimeUnits} from "../../ScheduleSettings/SettingsConfig";
import InfoTooltip from "../../../../../common/InfoTooltip";
import CustomCheckbox from '../../../../../common/CustomCheckbox';
import PickerField from '../../../../../common/PickerField';
import Box from '../../../../../common/Layout/Box';

const AutoActionsMessage = (props) => {
    const { values, errors, setFieldValue, touched, validationSchema, initProps } = props;
    const messageType = values.actionName ?? values.messageType;
    const trigger = AUTO_ACTION_TRIGGERS[messageType];
    const isReminder = messageType === AUTO_ACTION_TRIGGER_KEYS.REMINDER_SESSION;
    const isMissedTrialSession = messageType === AUTO_ACTION_TRIGGER_KEYS.MISSED_TRIAL_SESSION;
    const isAfterSessionNotify = messageType === AUTO_ACTION_TRIGGER_KEYS.AFTER_SESSION_NOTIFY || messageType === AUTO_ACTION_TRIGGER_KEYS.AFTER_SESSION_STAFF_NOTIFY;
    const isTrialSession = messageType === AUTO_ACTION_TRIGGER_KEYS.BEFORE_TRIAL_CLASS || messageType === AUTO_ACTION_TRIGGER_KEYS.AFTER_TRIAL_CLASS;
    const showCheckInSection = messageType === AUTO_ACTION_TRIGGER_KEYS.MARK_CHECK_IN;
    const showDigitalFormSection = ![AUTO_ACTION_TRIGGER_KEYS.MARK_CHECK_IN, AUTO_ACTION_TRIGGER_KEYS.APPOINTMENT_WAITLIST_OFFER].includes(messageType);
    const hideActiveToggle = messageType === AUTO_ACTION_TRIGGER_KEYS.APPOINTMENT_WAITLIST_OFFER;
    const allowTimeSelection = isReminder || isMissedTrialSession || isTrialSession;
    const applyTo = trigger?.applyTo;
    const timing = trigger?.timing;
    const copyKey = trigger?.copyKey;
    const checkIn = trigger?.checkIn;
    const hiddenMessageTypes = trigger?.hiddenChannels ?? [];
    const periodAmountTextBefore = initProps?.periodAmountTextBefore;
    const periodAmountTextAfter = initProps?.periodAmountTextAfter;
    const applyToRelation = useMemo(() => getAutoActionApplyToRelation(applyTo), [applyTo]);
    const dispatch = useDispatch();
    const box = useSelector(state => state.box?.box)
    const hasWhatapp = box?.integrations?.find(i => i.name === 'upgrade360' || i.name === 'greenApi' || i.name === 'metaWhatsapp')

    const [smsBalance, setSmsBalance] = useState(null);
    const [formOptions, setFormOptions] = useState([]);
    const { has, hasLock } = usePermissions()

    const isIL = useMemo(() => box?.locations_box[0]?.country_code === 'IL', [box]);
    const smsUnitCount = isIL ? 133 : 70;
    const maxCharCount = values.type?.some((item) => item?.id === PUSH) ? 175 : 1000;
    const charCount = useMemo(() => values?.message_content?.length ?? 0,[values?.message_content]);
    const hasMetaWhatsapp = useMemo(() => !!box?.integrations?.some(i => i.name === 'metaWhatsapp'), [box]);
    const isWhatsappSelected = useMemo(() => !!values.type?.some((item) => item?.id === WHATSAPP), [values.type]);
    const tags = useMemo(() => {
        const sessionTags = getAutoActionMessageTags(messageType, box.locations_box.length > 1, values.session_type);
        if (!hasMetaWhatsapp || !isWhatsappSelected) return sessionTags;
        return sessionTags.map(tag => tag.id === 'confirmation_link' ? {...tag, tooltip: t('confirmation-link-whatsapp-button-tooltip')} : tag);
    }, [messageType, values.session_type, hasMetaWhatsapp, isWhatsappSelected, box?.locations_box?.length]);
    const timingOptions = useMemo(() => (timing ? Object.entries(timing.options) : []), [timing]);
    const showTimingRadios = timingOptions.length > 1;
    const messageTypes = useMemo(() => {
        let options = [autoMessageTypesObj({})[PUSH]];
        if(has(`${EMAIL}Actions`) && !hasLock(`${EMAIL}Actions`)) options.push(autoMessageTypesObj({})[EMAIL])
        if(has(`${SMS}Actions`) && !hasLock(`${SMS}Actions`)) options.push(autoMessageTypesObj({smsBalance})[SMS])
        if(hasWhatapp) options.push(autoMessageTypesObj({})[WHATSAPP])
        if(hiddenMessageTypes.length > 0) options = options.filter(item => !hiddenMessageTypes.includes(item.id))
        return options
    }, [smsBalance, hiddenMessageTypes, hasWhatapp, has, hasLock]);
    const timeUnitOptions = useMemo(() => Object.keys(autoMessageTimeUnits).map((item) => ({id: item, name: t(`${item.toLowerCase()}s`)})),[])
    const autoActions = useSelector(state => state.box?.box?.auto_actions ?? [])

    useEffect(() => {
        getSmsBalance()
        getDigitalForms()
    }, []);

    const getSmsBalance = async () => {
        const res = await fetchApi('boxes/getSmsBalance', 'POST', null, false, true);
        if (values?.type?.some((item) => item?.id === SMS)) {
            const newTypes = values.type.filter(Boolean).map((item) => (item?.id === SMS ? autoMessageTypesObj({ smsBalance: res })[SMS] : item)).filter(Boolean);
            setFieldValue('type', newTypes);
        }
        setSmsBalance(res || 0);
    }

    const getDigitalForms = async () => {
        const res = await fetchApi('digitalForm/getAllForms', 'GET', null, false, true);
        if(res && res.length) {
            setFormOptions(res.map(df => ({...df, value: df.id, label: df.name})))
        }
    }

    const getInputLabel = (name, label) => {
        if(validationSchema && validationSchema.fields[name] && validationSchema.fields[name].spec?.presence === 'required') {
            return `${label}*`
        }
        return label
    }

    const onTagClick = (label) => {
        if (`${values.message_content || ''} [${label}]`.length <= maxCharCount) {
            setFieldValue('message_content', `${values.message_content || ''}[${label}]`)
        }
        document?.getElementById('text-area-message')?.focus()
    }

    const idsInOtherRules = useMemo(() => getMessageTypeAutoActions(autoActions, messageType, values.session_type)
        .filter((row) => row.message_template_id !== values.message_template_id)
        .flatMap((row) => buildAutoActionRelationFromRow(row, messageType).items.map((item) => item.id)), [autoActions, messageType, values.session_type, values.message_template_id])

    const openApplyToModal = () => dispatch(openModal({modalName: AUTO_ACTION_APPLY_TO_MODAL, props: {kind: applyTo, sessionType: showCheckInSection ? null : (trigger?.sessionType ?? values.session_type), hiddenServiceTypes: trigger?.hiddenServiceTypes, selectedItems: values.relation_items, idsInOtherRules, onSave: (items) => setFieldValue('relation_items', items)}}))

    const getTimingUnitOptions = (units) => (units ?? []).map((unit) => ({ id: unit, name: t(`${unit.toLowerCase()}s`) }));

    const getTimingSuffixKey = (caseValue) => {
        if (caseValue === 'BEFORE_EVENT') return 'TimingBefore';
        if (caseValue === 'IMMEDIATE') return 'TimingImmediate';
        if (caseValue === 'OCCASION_TIME_REFUSAL') return 'TimingAttempts';
        return 'TimingAfter';
    };

    const onTimingCaseChange = (caseValue) => {
        setFieldValue('case', caseValue);
        const option = timing?.options?.[caseValue];
        if (option?.sendTime && !values.hour) setFieldValue('hour', DEFAULT_TIMING_HOUR);
        // Immediate keeps the amount as-is (its input is just disabled); the save sends 0 for it.
        if (caseValue === 'IMMEDIATE') return;
        const unitOptions = option?.units ?? [];
        if (!unitOptions.length && !option?.countedIn) return;
        const defaultAmount = getDefaultPeriodAmount(messageType);
        const defaultUnit = getDefaultPeriodTimeUnit(messageType);
        const shouldApplyDefaults = values.period_amount === 0 || values.period_amount == null;
        if (shouldApplyDefaults) setFieldValue('period_amount', defaultAmount ?? 1);
        const nextUnit = unitOptions.includes(values.period_time_unit) ? values.period_time_unit : (defaultUnit && unitOptions.includes(defaultUnit) ? defaultUnit : unitOptions[0]);
        if (nextUnit) setFieldValue('period_time_unit', nextUnit);
    };

    const onTimingHourChange = (time) => setFieldValue('hour', moment(time).format(dbTimeFormat));

    return (
        <VStack space={'0.8rem'} style={{ paddingInlineEnd: '5px', boxSizing: 'border-box' }}>
            {!hideActiveToggle &&
            <HStack justifyContent={'space-between'} alignItems={'center'}>
                <AppText fontSize={GlobalStyleAttributes.fonts.standard16} mediumFont={!isMobile} boldFont={isMobile}>{t('active-reminder')}</AppText>
                <CustomSwitch isChecked={values.active} onToggle={(val) => setFieldValue('active', val)}/>
            </HStack>}
            {allowTimeSelection && values.active &&
                <VStack>
                    <HStack space={'0.5rem'} alignItems={'center'} style={{flexFlow: 'wrap', rowGap: '0.2rem'}}>
                        <AppText fontSize={GlobalStyleAttributes.fonts.heading3} color={Colors.newDesign.mainText}>{periodAmountTextBefore || t('period-amount-text-before')}</AppText>
                        <HStack space={'0.5rem'} alignItems={'center'}>
                            <SimpleInput value={values.period_amount}
                                         onChangeCallback={(val) => setFieldValue('period_amount', val)}
                                         numericOnly={true}
                                         width={'4rem'}
                                         status={errors.period_amount && touched.period_amount ? 'error' : null}/>
                            <DropDownMenu title={t(`${values.period_time_unit?.toLowerCase()}s`)}
                                          items={timeUnitOptions}
                                          onPress={(val) => setFieldValue('period_time_unit', val)}
                                          showBorder={true}
                                          styleOverride={{height: '30px', width: '4.5rem'}}
                                          fontSize={GlobalStyleAttributes.fonts.standard16}/>
                            <HStack alignItems={'center'}>
                                <AppText fontSize={GlobalStyleAttributes.fonts.heading3} color={Colors.newDesign.mainText}>{periodAmountTextAfter || t('period-amount-text-after')}</AppText>
                            </HStack>
                        </HStack>
                    </HStack>
                    {touched.period_amount && errors.period_amount && <AppText color={Colors.error} fontSize={GlobalStyleAttributes.fonts.infoText} mediumFont>{errors.period_amount}</AppText>}
                </VStack>
            }
            {timing && values.active &&
                <VStack space={'0.5rem'}>
                    {timingOptions.map(([caseValue, option]) => {
                        const isSelected = values.case === caseValue;
                        const hasAmountInput = Boolean(option.units || option.countedIn);
                        const isAttempts = option.countedIn === 'attempts';
                        const unitOptions = getTimingUnitOptions(option.units);
                        const suffixKey = getTimingSuffixKey(caseValue);
                        return (
                            <HStack key={caseValue} space={'0.5rem'} alignItems={'center'} style={{ flexFlow: 'wrap', rowGap: '0.2rem' }}>
                                {showTimingRadios && <Radio checked={isSelected} onChange={() => onTimingCaseChange(caseValue)}/>}
                                {hasAmountInput && <AppText fontSize={GlobalStyleAttributes.fonts.heading3} color={Colors.newDesign.mainText}>{t(option.countedIn ? 'autoActionTimingSendTheMessageAfter' : 'autoActionTimingSendTheMessage')}</AppText>}
                                {hasAmountInput &&
                                    <SimpleInput value={isAttempts ? values.count : values.period_amount}
                                                 onChangeCallback={(val) => setFieldValue(isAttempts ? 'count' : 'period_amount', val)}
                                                 numericOnly={true}
                                                 width={'4rem'}
                                                 disabled={!isSelected}
                                                 status={(isAttempts ? errors.count && touched.count : errors.period_amount && touched.period_amount) ? 'error' : null}/>}
                                {option.units &&
                                    <DropDownMenu title={t(`${(values.period_time_unit ?? option.units[0])?.toLowerCase()}s`)}
                                                  items={unitOptions}
                                                  onPress={(val) => setFieldValue('period_time_unit', val)}
                                                  showBorder={true}
                                                  styleOverride={{ height: '30px', width: '4.5rem', opacity: isSelected ? 1 : 0.5, pointerEvents: isSelected ? 'auto' : 'none' }}
                                                  fontSize={GlobalStyleAttributes.fonts.standard16}/>}
                                <AppText fontSize={GlobalStyleAttributes.fonts.heading3} color={Colors.newDesign.mainText}>{t(`${copyKey}${suffixKey}`)}</AppText>
                                {option.sendTime &&
                                    <SharedTimepicker type={'time'}
                                                      value={values.hour ? moment(values.hour, dbTimeFormat) : moment(DEFAULT_TIMING_HOUR, dbTimeFormat)}
                                                      onChange={onTimingHourChange}
                                                      isDisabled={!isSelected}
                                                      inputStyle={{ fontSize: GlobalStyleAttributes.fonts.standard16, width: '81px' }}/>}
                            </HStack>
                        );
                    })}
                    {touched.period_amount && errors.period_amount && <AppText color={Colors.error} fontSize={GlobalStyleAttributes.fonts.infoText} mediumFont>{errors.period_amount}</AppText>}
                    {touched.period_time_unit && errors.period_time_unit && <AppText color={Colors.error} fontSize={GlobalStyleAttributes.fonts.infoText} mediumFont>{errors.period_time_unit}</AppText>}
                    {touched.hour && errors.hour && <AppText color={Colors.error} fontSize={GlobalStyleAttributes.fonts.infoText} mediumFont>{errors.hour}</AppText>}
                    {touched.count && errors.count && <AppText color={Colors.error} fontSize={GlobalStyleAttributes.fonts.infoText} mediumFont>{errors.count}</AppText>}
                </VStack>
            }
            {checkIn && values.active &&
                <CustomCheckbox text={
                    <HStack alignItems={'center'}>
                        <AppText fontSize={GlobalStyleAttributes.fonts.standard16} color={Colors.newDesign.mainText}>{t(checkIn.labelKey)}</AppText>
                        <InfoTooltip title={t(`${copyKey}CheckInTooltip`)}/>
                    </HStack>
                } isChecked={!!values.count} onChange={(val) => setFieldValue('count', Number(val))}/>}
            {showCheckInSection && values.active &&
                <VStack space={'0.2rem'}>
                    <HStack space={'0.5rem'} alignItems={'center'}>
                        <AppText>{t('check-in-reminder-before')}</AppText>
                        <SimpleInput value={values.period_amount}
                                     onChangeCallback={(val) => setFieldValue('period_amount', val)}
                                     onBlur={() => setFieldValue('period_amount', values.period_amount - (values.period_amount % 5))}
                                     numericOnly={true}
                                     width={'4rem'}
                                     step={5}
                                     min={5}
                                     max={60}
                                     status={errors.period_amount ? 'error' : null}
                        />
                        <AppText>{t('check-in-reminder-after')}</AppText>
                    </HStack>
                    <CustomCheckbox text={t('only-when-unchecked')} isChecked={values.count} onChange={(val) => setFieldValue('count', Number(val))}/>
                </VStack>
            }
            {isAfterSessionNotify && values.active &&
                <VStack space={'0.2rem'}>
                    <HStack space={'0.5rem'} alignItems={'center'} style={{flexFlow: 'wrap', rowGap: '0.2rem'}}>
                        <AppText fontSize={GlobalStyleAttributes.fonts.heading3} color={Colors.newDesign.mainText}>{t('after-session-period-amount-before')}</AppText>
                        <SimpleInput value={values.period_amount}
                                     onChangeCallback={(val) => setFieldValue('period_amount', val)}
                                     numericOnly={true}
                                     width={'4rem'}
                                     min={0}
                                     status={errors.period_amount && touched.period_amount ? 'error' : null} />
                        <AppText fontSize={GlobalStyleAttributes.fonts.heading3} color={Colors.newDesign.mainText}>{t('after-session-period-amount-after')}</AppText>
                    </HStack>
                    {touched.period_amount && errors.period_amount && <AppText color={Colors.error} fontSize={GlobalStyleAttributes.fonts.infoText} mediumFont>{errors.period_amount}</AppText>}
                    <CustomCheckbox text={
                        <HStack alignItems={'center'}>
                            <AppText fontSize={GlobalStyleAttributes.fonts.standard16} color={Colors.newDesign.mainText}>{t('after-session-only-when-checked-in')}</AppText>
                            <InfoTooltip title={t('after-session-only-when-checked-in-tooltip')}/>
                        </HStack>
                    } isChecked={!!values.count} onChange={(val) => setFieldValue('count', Number(val))}/>
                </VStack>
            }
            {!hideActiveToggle && <Divider orientation="horizontal" h={'1px'} w={'100%'} bg={Colors.borderColor} style={{marginBlock: '0.3rem'}}/>}
            {isMobile && <AppText fontSize={GlobalStyleAttributes.fonts.standard16} boldFont>{t('edit-template')}</AppText>}
            <VStack space={'0.2rem'}>
                <AppText fontSize={GlobalStyleAttributes.fonts.infoText} mediumFont>{getInputLabel('type', t('send-via'))}</AppText>
                <AdvanceSelect options={messageTypes}
                               isMulti={true}
                               onChange={(val) => setFieldValue('type', Array.isArray(val) ? val.filter(Boolean) : [])}
                               initValue={values?.type ?? []}
                               closeMenuOnSelect={false} />
                <AppText fontSize={GlobalStyleAttributes.fonts.infoText} color={Colors.error}>{errors.type}</AppText>
            </VStack>
            {applyToRelation &&
            <VStack space={'0.2rem'}>
                <AppText fontSize={GlobalStyleAttributes.fonts.infoText} mediumFont>{t(applyToRelation.title)}</AppText>
                <VStack space={'12px'}>
                    <Box style={{ width: 'fit-content', alignSelf: 'flex-start' }}>
                        <Segmented block={false} options={[
                            {value: 'all', label: t(applyToRelation.scopeAll)},
                            {value: 'specific', label: t(applyToRelation.scopeSpecific)}]}
                            value={values.relation_kind}
                            onChange={(val) => setFieldValue('relation_kind', val)}/>
                    </Box>
                    {values.relation_kind === 'specific' && <PickerField items={values.relation_items?.map(item => item.name)} placeholder={t(applyToRelation.selectPlaceholder)} onPress={openApplyToModal}/>}
                </VStack>
                {values.relation_kind === 'specific'
                    ? <AppText fontSize={GlobalStyleAttributes.fonts.infoText} color={Colors.newDesign.darkGrey}>{t(applyToRelation.specificHelper)}</AppText>
                    : <AppText fontSize={GlobalStyleAttributes.fonts.infoText} color={Colors.newDesign.darkGrey}>{t(applyToRelation.allHelper)}</AppText>}
            </VStack>}
            {(has(`${EMAIL}Actions`) && !hasLock(`${EMAIL}Actions`)) &&
            <VStack space={'0.2rem'}>
                <AppText fontSize={GlobalStyleAttributes.fonts.infoText} mediumFont>{t('subject')}</AppText>
                <SimpleInput value={values.subject}
                             onChangeCallback={(val) => setFieldValue('subject', val)}
                             error={(touched.subject && errors.subject) || null}
                             width={'100%'}
                             maxLength={40}
                             showCount
                             placeholder={t('subject-placeholder')} />
                <AppText fontSize={GlobalStyleAttributes.fonts.infoText} color={Colors.error}>{errors.subject}</AppText>
            </VStack>}
            {showDigitalFormSection && <VStack space={'0.2rem'}>
                <AppText fontSize={GlobalStyleAttributes.fonts.infoText} mediumFont>{getInputLabel('digital_form_id', t('form-name'))}</AppText>
                <AdvanceSelect options={formOptions}
                               initValue={values.digital_form_id}
                               onChange={(val) => setFieldValue('digital_form_id', val?.id ?? null)}
                               isClearable={true}/>
                <AppText fontSize={GlobalStyleAttributes.fonts.infoText} color={Colors.error}>{errors.digital_form_id}</AppText>
            </VStack>}
            <MessageTextFormInput {...{tags, onTagClick, setFieldValue, values, maxLength: maxCharCount, smsUnitCount,
                touched, errors, name: 'message_content', placeholder: t('add-message-here'), id: 'text-area-message',
                isSms: values.type?.some((item) => item?.id === SMS), showDigitalFormText: values.digital_form_id, charCount, maxCharCount, absoluteCharCount: true}} containerProps={{mb: '1rem'}}/>
        </VStack>
    );
};

export default AutoActionsMessage;
